import * as pvtsutils from "pvtsutils";

// Pattern für PEM ohne Einränkungen für Anfang und Ende oder Zeilenlänge #2023
const PEM_PATTERN =
  /-----BEGIN [ A-Z]+-----\r?\n([A-Za-z0-9+/]+\r?\n)*[A-Za-z0-9+/]+={0,3}\r?\n-----END [ A-Z]+-----/;


/**
 * Prüft, ob eine Eingabe ein String ist. Falls nicht, wird ein TypeError
 * geworfen.
 *
 * Beispiel: checkString('key in myCryptoFunction', 42) gibt
 * TypeError('key in myCryptoFunction ist kein String sondern: 42')
 *
 * @param {string} name Bezeichner für den zu prüfenden Wert
 * @param {string} input der zu prüfende Wert
 */
export function checkString(name, input) {
  if (typeof input !== "string") {
    throw new TypeError(
      name + " ist kein String sondern: " + JSON.stringify(input)
    );
  }
}

/**
 * Prüft, ob ein String eine PEM-Nachricht ist. Falls nicht, wird ein TypeError
 * geworfen.
 *
 * Zeilenenden können entweder \r\n oder \n sein.
 *
 * Beispiel: checkPEM('key in myCryptoFunction', 'kein Key') gibt
 * TypeError('key in myCryptoFunction ist kein PEM sondern: "kein Key"')
 *
 * @param {string} name Bezeichner für den zu prüfenden Wert
 * @param {string} pem das zu prüfende PEM
 */
export function checkPEM(name, pem) {
  checkString(name, pem);

  const pattern = new RegExp("^" + PEM_PATTERN.source + "\\r?\\n$");

  if (!pem.match(pattern)) {
    throw new TypeError(name + " ist kein PEM sondern: " + JSON.stringify(pem));
  }
}

/**
 * Extrahiert aus einem Text die erste PEM-Nachricht.
 *
 * Gesucht wird anhand des Musters PEM_PATTERN.
 *
 * Falls kein PEM gefunden wird, wird ein TypeError geworfen.
 *
 * Zeilenenden können entweder \r\n oder \n sein.
 *
 * @param {string} name Bezeichner für den Text (nur wichtig für Fehler)
 * @param {string} text der Text, in dem die PEM-Nachricht gesucht wird
 * @returns {string} extrahiertes PEM mit abschließendem \n
 */
export function extractPEM(name, text) {
  checkString(name, text);

  const result = text.match(PEM_PATTERN);

  if (!result) {
    throw new TypeError(
      name + " enthält kein PEM sondern: " + JSON.stringify(text)
    );
  }

  // Gefundenes PEM ist vollständiger Match des regulären Ausdrucks
  const pem = result[0] + "\n";

  checkPEM("Extrahiertes PEM aus " + name, pem);

  return pem;
}

/**
 * Formats a PEM-String into an ArrayBuffer.
 * @param {String} PEM formatted String
 * @returns ArrayBuffer of PEM
 */
export function fromPEM(pem) {
  const base64 = pem
    .replace(/-{5}(BEGIN|END) .*-{5}/gm, "")
    .replace(/\s/gm, "");
  return pvtsutils.Convert.FromBase64(base64);
}

/**
 * Formats a BufferSource into a PEM-String with fitting tags.
 * @param {BufferSource} buffer Content to format
 * @param {String} tag Name for the tags
 * @returns PEM formatted String
*/
export function toPEM(buffer, tag) {
    return [
      `-----BEGIN ${tag}-----`,
      formatPEM(pvtsutils.Convert.ToBase64(buffer)),
      `-----END ${tag}-----`,
      "",
    ].join("\n");
}

/**
   * Format string in order to have each line with length equal to 64.
   * Methode aus PKIjs test utils.
   * @param pemString String to format
   * @returns Formatted string
   */
export function formatPEM(pemString) {
  const PEM_STRING_LENGTH = pemString.length,
    LINE_LENGTH = 64;
  const wrapNeeded = PEM_STRING_LENGTH > LINE_LENGTH;

  if (wrapNeeded) {
    let formattedString = "",
      wrapIndex = 0;

    for (let i = LINE_LENGTH; i < PEM_STRING_LENGTH; i += LINE_LENGTH) {
      formattedString += pemString.substring(wrapIndex, i) + "\r\n";
      wrapIndex = i;
    }

    formattedString += pemString.substring(wrapIndex, PEM_STRING_LENGTH);
    return formattedString;
  } else {
    return pemString;
  }
}